import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import {
  AlertTriangle,
  Loader2,
  Search,
  Wrench,
  RotateCcw,
  ShieldAlert,
  CheckCircle2,
  Copy,
  IndianRupee,
} from 'lucide-react';
import { toast } from 'sonner';
import { getErrorMessage } from '../services/api';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/** Render a rupee amount with smart suffixes — same logic as the dashboard tile. */
const fmtRupees = (n) => {
  const num = Number(n || 0);
  if (Number.isNaN(num) || num === 0) return '—';
  if (num >= 1e7) return `₹${(num / 1e7).toFixed(2)} Cr`;
  if (num >= 1e5) return `₹${(num / 1e5).toFixed(2)} L`;
  if (num >= 1e3) return `₹${(num / 1e3).toFixed(1)} K`;
  return `₹${num}`;
};

/**
 * Admin diagnostic page for CTC outliers — finds applications/candidates whose
 * stored CTC is unreasonably large (most likely a unit mistake) and lets the
 * admin patch each one inline. Worst offenders first.
 */
const CtcCleanup = () => {
  const [threshold, setThreshold] = useState(500000000); // ₹50 Cr default
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState({ applications: [], candidates: [], threshold: 0 });
  const [savingKey, setSavingKey] = useState(null);
  const [editValue, setEditValue] = useState({});

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/ctc-outliers`, {
        params: { threshold, limit: 100 },
      });
      setData(res.data);
    } catch (err) {
      toast.error(getErrorMessage(err, 'Failed to load outliers'));
    } finally {
      setLoading(false);
    }
  }, [threshold]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // ----- Missing-CTC section (Offered + Joined apps without offered_ctc) -----
  const [missing, setMissing] = useState({ offered_count: 0, joined_count: 0, total: 0, items: [] });
  const [missingLoading, setMissingLoading] = useState(false);
  const [missingDraft, setMissingDraft] = useState({}); // application_id -> string (lakhs)
  const [missingSavingId, setMissingSavingId] = useState(null);

  const fetchMissing = useCallback(async () => {
    setMissingLoading(true);
    try {
      const res = await axios.get(`${API}/admin/ctc-missing`, { params: { limit: 500 } });
      setMissing(res.data || { offered_count: 0, joined_count: 0, total: 0, items: [] });
    } catch (err) {
      toast.error(getErrorMessage(err, 'Failed to load missing-CTC list'));
    } finally {
      setMissingLoading(false);
    }
  }, []);

  useEffect(() => { fetchMissing(); }, [fetchMissing]);

  const saveMissingCtc = async (it) => {
    const raw = String(missingDraft[it.application_id] ?? '').trim();
    const n = Number(raw);
    if (raw === '' || Number.isNaN(n) || n <= 0) {
      toast.error('Enter Offered CTC in Lakhs (positive number)');
      return;
    }
    setMissingSavingId(it.application_id);
    try {
      // PATCH /applications/{id}/offered-ctc expects rupees.
      await axios.patch(`${API}/applications/${it.application_id}/offered-ctc`, {
        offered_ctc: Math.round(n * 100000),
      });
      toast.success(`Saved ₹${n} L for ${it.candidate_name}`);
      // Optimistic remove
      setMissing((prev) => ({
        ...prev,
        items: prev.items.filter((x) => x.application_id !== it.application_id),
        offered_count: it.stage === 'offer_released' ? Math.max(0, prev.offered_count - 1) : prev.offered_count,
        joined_count: it.stage === 'joined' ? Math.max(0, prev.joined_count - 1) : prev.joined_count,
        total: Math.max(0, prev.total - 1),
      }));
      setMissingDraft((prev) => {
        const next = { ...prev };
        delete next[it.application_id];
        return next;
      });
      try {
        window.dispatchEvent(new CustomEvent('ctc:backfilled', { detail: { application_id: it.application_id } }));
      } catch { /* ignore */ }
    } catch (err) {
      toast.error(getErrorMessage(err, 'Failed to save offered CTC'));
    } finally {
      setMissingSavingId(null);
    }
  };

  // One-shot ÷100K across every outlier in the org. Confirmation is a
  // simple window.confirm — bulk destructive enough to warrant a click-
  // through, light enough to not need a full Dialog component.
  const [bulkRunning, setBulkRunning] = useState(false);
  const bulkFix = async () => {
    const total = (data.applications?.length || 0) +
      (data.candidates || []).reduce((acc, c) => {
        let n = 0;
        if (c.expected_ctc > threshold) n += 1;
        if (c.current_ctc > threshold) n += 1;
        return acc + n;
      }, 0);
    if (total === 0) return;
    // eslint-disable-next-line no-restricted-globals, no-alert
    if (!window.confirm(`Divide ${total} CTC value${total === 1 ? '' : 's'} by 100,000?\n\nRecords whose result would still exceed ₹50 Cr will be skipped (you can fix those manually).\n\nThis is fully audited.`)) {
      return;
    }
    setBulkRunning(true);
    try {
      const res = await axios.post(`${API}/admin/ctc-outliers/bulk-fix`, null, {
        params: { operation: 'divide_100k', threshold },
      });
      const fixed = res.data?.total_fixed || 0;
      const skipped = res.data?.total_skipped || 0;
      toast.success(`Bulk fix complete · ${fixed} cleaned${skipped ? ` · ${skipped} skipped (needs manual review)` : ''}`);
      fetchData();
    } catch (err) {
      toast.error(getErrorMessage(err, 'Bulk fix failed'));
    } finally {
      setBulkRunning(false);
    }
  };

  const patchApp = async (app, body) => {
    const key = `app-${app.id}`;
    setSavingKey(key);
    try {
      const res = await axios.patch(`${API}/admin/ctc-outliers/application/${app.id}`, body);
      toast.success(`Patched ${app.candidate_name || 'application'}: ${fmtRupees(res.data.old_value)} → ${fmtRupees(res.data.new_value)}`);
      // Optimistic: drop the row if new value is below the current threshold
      if (res.data.new_value <= threshold) {
        setData((prev) => ({
          ...prev,
          applications: prev.applications.filter((a) => a.id !== app.id),
        }));
      } else {
        fetchData();
      }
    } catch (err) {
      toast.error(getErrorMessage(err, 'Failed to patch'));
    } finally {
      setSavingKey(null);
    }
  };

  const patchCand = async (cand, field, body) => {
    const key = `cand-${cand.id}-${field}`;
    setSavingKey(key);
    try {
      const res = await axios.patch(`${API}/admin/ctc-outliers/candidate/${cand.id}?field=${field}`, body);
      toast.success(`Patched ${cand.name || 'candidate'} (${field}): ${fmtRupees(res.data.old_value)} → ${fmtRupees(res.data.new_value)}`);
      fetchData();
    } catch (err) {
      toast.error(getErrorMessage(err, 'Failed to patch'));
    } finally {
      setSavingKey(null);
    }
  };

  // Copy the candidate's most-recent sane offered_ctc to their expected_ctc.
  // Backend picks the source application; we just trigger and report.
  const backfillCandFromApp = async (cand) => {
    const key = `cand-${cand.id}-backfill`;
    setSavingKey(key);
    try {
      const res = await axios.post(`${API}/admin/ctc-outliers/candidate/${cand.id}/backfill-from-application`);
      const src = res.data.source_application;
      toast.success(
        `${cand.name || 'Candidate'}: ${fmtRupees(res.data.old_value)} → ${fmtRupees(res.data.new_value)} (from ${src?.job_title || 'application'})`,
      );
      fetchData();
    } catch (err) {
      toast.error(getErrorMessage(err, 'Backfill failed'));
    } finally {
      setSavingKey(null);
    }
  };

  return (
    <div className="space-y-6 p-4 sm:p-6 max-w-6xl mx-auto" data-testid="ctc-cleanup-page">
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 rounded-lg bg-rose-50 border border-rose-200 flex items-center justify-center flex-shrink-0">
          <ShieldAlert className="w-5 h-5 text-rose-600" />
        </div>
        <div className="flex-1">
          <h1 className="text-2xl font-semibold text-slate-900">CTC Cleanup</h1>
          <p className="text-sm text-slate-600 mt-1">
            Find application and candidate records whose stored CTC is impossibly large (almost always a unit mistake — extra ×100,000 from a double-run of the LPA→rupees backfill, or a typo). Patch each row in place. Ceiling defaults to <strong>₹50 Cr</strong>.
          </p>
        </div>
      </div>

      <Card className="p-4 flex items-center gap-3 flex-wrap">
        <label className="text-sm text-slate-700 font-medium">Threshold (rupees):</label>
        <Input
          type="number"
          value={threshold}
          onChange={(e) => setThreshold(Number(e.target.value) || 0)}
          className="w-[180px]"
          data-testid="ctc-cleanup-threshold-input"
        />
        <span className="text-xs text-slate-500">= {fmtRupees(threshold)}</span>
        <Button
          onClick={fetchData}
          disabled={loading || bulkRunning}
          data-testid="ctc-cleanup-refresh-btn"
          className="ml-auto bg-slate-600 hover:bg-slate-700 text-white"
        >
          {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Search className="w-4 h-4 mr-2" />}
          Scan now
        </Button>
        <Button
          onClick={bulkFix}
          disabled={loading || bulkRunning || ((data.applications?.length || 0) + (data.candidates?.length || 0) === 0)}
          data-testid="ctc-cleanup-bulk-fix-btn"
          className="bg-emerald-600 hover:bg-emerald-700 text-white"
          title="Divide every CTC value above the threshold by 100,000 in one shot. Skips rows whose result would still be impossibly large."
        >
          {bulkRunning ? (
            <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Fixing…</>
          ) : (
            <>Fix all ÷ 100K</>
          )}
        </Button>
      </Card>

      {/* Missing CTC — Offered + Joined apps without offered_ctc on file.
          Strictly checks offered_ctc null/0/absent, no candidate-profile fallback. */}
      <Card className="p-4" data-testid="ctc-cleanup-missing-section">
        <div className="flex items-center gap-2 mb-3 flex-wrap">
          <IndianRupee className="w-4 h-4 text-amber-600" />
          <h2 className="text-base font-semibold text-slate-900">
            Missing CTC · {missing.total}
          </h2>
          <Badge variant="outline" className="text-[10px] border-amber-300 text-amber-800 bg-amber-50">
            {missing.offered_count} offered · {missing.joined_count} joined
          </Badge>
          <Button
            variant="outline"
            size="sm"
            onClick={fetchMissing}
            disabled={missingLoading}
            data-testid="ctc-cleanup-missing-refresh-btn"
            className="ml-auto h-8"
          >
            {missingLoading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Search className="w-3.5 h-3.5" />}
            <span className="ml-1.5">Rescan</span>
          </Button>
        </div>
        <p className="text-xs text-slate-500 mb-3">
          Offered &amp; Joined applications without an Offered CTC on file. Backfill below — saved values immediately update your Joined CTC totals.
        </p>
        {missingLoading && missing.items.length === 0 ? (
          <div className="flex items-center gap-2 text-sm text-slate-500 py-4">
            <Loader2 className="w-4 h-4 animate-spin" /> Loading…
          </div>
        ) : missing.total === 0 ? (
          <div className="text-sm text-emerald-700 flex items-center gap-2 py-4">
            <CheckCircle2 className="w-4 h-4" />
            Every Offered &amp; Joined application has CTC on file. Nice.
          </div>
        ) : (
          <div className="divide-y divide-slate-100">
            {missing.items.map((it) => {
              const saving = missingSavingId === it.application_id;
              const draft = missingDraft[it.application_id] ?? '';
              return (
                <div
                  key={it.application_id}
                  className="py-2.5 flex items-center gap-3 flex-wrap"
                  data-testid={`ctc-cleanup-missing-row-${it.application_id}`}
                >
                  <Badge
                    variant="outline"
                    className={`text-[10px] uppercase ${
                      it.stage === 'joined'
                        ? 'border-emerald-300 text-emerald-700 bg-emerald-50'
                        : 'border-teal-300 text-teal-700 bg-teal-50'
                    }`}
                  >
                    {it.stage === 'joined' ? 'Joined' : 'Offered'}
                  </Badge>
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-semibold text-slate-900 truncate">
                      {it.candidate_name}
                    </div>
                    <div className="text-xs text-slate-500 truncate">
                      {it.job_title}
                      {it.client_name ? ` · ${it.client_name}` : ''}
                    </div>
                  </div>
                  <div className="flex items-center gap-1.5 flex-shrink-0">
                    <span className="text-xs text-slate-500">₹</span>
                    <Input
                      type="number"
                      step="0.01"
                      min="0"
                      placeholder="e.g. 12.5"
                      value={draft}
                      onChange={(e) =>
                        setMissingDraft((prev) => ({
                          ...prev,
                          [it.application_id]: e.target.value,
                        }))
                      }
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          saveMissingCtc(it);
                        }
                      }}
                      className="h-8 w-24 text-sm"
                      disabled={saving}
                      data-testid={`ctc-cleanup-missing-input-${it.application_id}`}
                    />
                    <span className="text-[10px] text-slate-500 mr-1">L</span>
                    <Button
                      size="sm"
                      disabled={saving || !draft}
                      onClick={() => saveMissingCtc(it)}
                      data-testid={`ctc-cleanup-missing-save-${it.application_id}`}
                      className="h-8 px-3 bg-amber-600 hover:bg-amber-700 text-white"
                    >
                      {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : 'Save'}
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </Card>

      {/* Applications */}
      <Card className="p-4">
        <div className="flex items-center gap-2 mb-3">
          <AlertTriangle className="w-4 h-4 text-amber-600" />
          <h2 className="text-base font-semibold text-slate-900">Applications · {data.applications?.length || 0}</h2>
        </div>
        {(!data.applications || data.applications.length === 0) ? (
          <div className="text-sm text-emerald-700 flex items-center gap-2 py-4">
            <CheckCircle2 className="w-4 h-4" />
            No applications above the threshold. Joined CTC tile is safe.
          </div>
        ) : (
          <div className="divide-y divide-slate-100">
            {data.applications.map((a) => {
              const key = `app-${a.id}`;
              const isSaving = savingKey === key;
              const inputVal = editValue[key] ?? '';
              return (
                <div key={a.id} className="py-3 flex items-start gap-3 flex-wrap" data-testid={`ctc-cleanup-app-row-${a.id}`}>
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-semibold text-slate-900">{a.candidate_name || 'Unknown'}</div>
                    <div className="text-xs text-slate-500 truncate">
                      {a.job_title || '—'}{a.client_name ? ` · ${a.client_name}` : ''}
                    </div>
                    <div className="text-[11px] text-slate-400 mt-0.5">
                      <Badge variant="outline" className="text-[10px]">{(a.stage || '').replace(/_/g, ' ')}</Badge>
                      <span className="ml-2">App: {a.id.slice(0, 8)}…</span>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="font-mono font-bold text-rose-700 text-base">{fmtRupees(a.offered_ctc)}</div>
                    <div className="text-[10px] text-slate-500">raw: {a.offered_ctc?.toLocaleString('en-IN')}</div>
                    {a.suggested_offered_ctc != null && (
                      <div className="text-[10px] text-emerald-700 mt-0.5">
                        ÷100K → {fmtRupees(a.suggested_offered_ctc)}
                      </div>
                    )}
                  </div>
                  <div className="flex items-center gap-1.5">
                    <Button
                      size="sm"
                      variant="outline"
                      disabled={isSaving}
                      onClick={() => patchApp(a, { operation: 'divide_100k' })}
                      data-testid={`ctc-cleanup-app-${a.id}-divide-btn`}
                      title="Divide current value by 100,000 — undoes a double LPA→rupees backfill"
                      className="border-emerald-200 text-emerald-700 hover:bg-emerald-50"
                    >
                      <RotateCcw className="w-3 h-3 mr-1" />
                      ÷ 100K
                    </Button>
                    <Input
                      type="number"
                      placeholder="New value (rupees)"
                      value={inputVal}
                      onChange={(e) => setEditValue({ ...editValue, [key]: e.target.value })}
                      className="h-8 w-[140px] text-xs"
                      data-testid={`ctc-cleanup-app-${a.id}-input`}
                    />
                    <Button
                      size="sm"
                      disabled={isSaving || !inputVal}
                      onClick={() => patchApp(a, { new_value: Number(inputVal) })}
                      data-testid={`ctc-cleanup-app-${a.id}-save-btn`}
                      className="bg-rose-600 hover:bg-rose-700 text-white"
                    >
                      {isSaving ? <Loader2 className="w-3 h-3 animate-spin" /> : <Wrench className="w-3 h-3" />}
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </Card>

      {/* Candidates */}
      <Card className="p-4">
        <div className="flex items-center gap-2 mb-3">
          <AlertTriangle className="w-4 h-4 text-amber-600" />
          <h2 className="text-base font-semibold text-slate-900">Candidates · {data.candidates?.length || 0}</h2>
        </div>
        {(!data.candidates || data.candidates.length === 0) ? (
          <div className="text-sm text-emerald-700 flex items-center gap-2 py-4">
            <CheckCircle2 className="w-4 h-4" />
            No candidate profiles above the threshold.
          </div>
        ) : (
          <div className="divide-y divide-slate-100">
            {data.candidates.map((c) => {
              const fields = [
                { field: 'expected_ctc', value: c.expected_ctc, suggested: c.suggested_expected_ctc },
                { field: 'current_ctc', value: c.current_ctc, suggested: c.suggested_current_ctc },
              ].filter((f) => f.value != null && f.value > threshold);
              return (
                <div key={c.id} className="py-3" data-testid={`ctc-cleanup-cand-row-${c.id}`}>
                  <div className="flex items-center gap-2 mb-1.5 flex-wrap">
                    <div className="text-sm font-semibold text-slate-900">{c.name || 'Unknown'} <span className="text-[10px] font-normal text-slate-400">{c.id.slice(0, 8)}…</span></div>
                    {c.has_sane_application ? (
                      <Button
                        size="sm"
                        variant="outline"
                        disabled={savingKey === `cand-${c.id}-backfill`}
                        onClick={() => backfillCandFromApp(c)}
                        data-testid={`ctc-cleanup-cand-${c.id}-backfill-btn`}
                        title="Copy this candidate's most-recent sane offered_ctc from their applications into expected_ctc"
                        className="h-7 px-2 text-[11px] font-semibold border-teal-200 text-teal-700 hover:bg-teal-50"
                      >
                        {savingKey === `cand-${c.id}-backfill` ? (
                          <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                        ) : (
                          <Copy className="w-3 h-3 mr-1" />
                        )}
                        Backfill expected_ctc from application
                      </Button>
                    ) : (
                      <span
                        className="text-[10px] text-slate-400"
                        title="No application with a sane offered_ctc exists for this candidate — fix manually with ÷ 100K or New value"
                      >
                        no sane application on file
                      </span>
                    )}
                  </div>
                  {fields.map(({ field, value, suggested }) => {
                    const key = `cand-${c.id}-${field}`;
                    const isSaving = savingKey === key;
                    const inputVal = editValue[key] ?? '';
                    return (
                      <div key={field} className="flex items-center gap-3 flex-wrap py-1.5 ml-3 border-l-2 border-rose-100 pl-3">
                        <div className="text-xs font-semibold text-slate-600 w-[110px]">{field}</div>
                        <div className="text-right">
                          <div className="font-mono font-bold text-rose-700 text-sm">{fmtRupees(value)}</div>
                          <div className="text-[10px] text-slate-500">raw: {value?.toLocaleString('en-IN')}</div>
                          {suggested != null && (
                            <div className="text-[10px] text-emerald-700">÷100K → {fmtRupees(suggested)}</div>
                          )}
                        </div>
                        <div className="flex items-center gap-1.5 ml-auto">
                          <Button
                            size="sm"
                            variant="outline"
                            disabled={isSaving}
                            onClick={() => patchCand(c, field, { operation: 'divide_100k' })}
                            data-testid={`ctc-cleanup-cand-${c.id}-${field}-divide-btn`}
                            className="border-emerald-200 text-emerald-700 hover:bg-emerald-50"
                          >
                            <RotateCcw className="w-3 h-3 mr-1" /> ÷ 100K
                          </Button>
                          <Input
                            type="number"
                            placeholder="New value"
                            value={inputVal}
                            onChange={(e) => setEditValue({ ...editValue, [key]: e.target.value })}
                            className="h-8 w-[140px] text-xs"
                            data-testid={`ctc-cleanup-cand-${c.id}-${field}-input`}
                          />
                          <Button
                            size="sm"
                            disabled={isSaving || !inputVal}
                            onClick={() => patchCand(c, field, { new_value: Number(inputVal) })}
                            data-testid={`ctc-cleanup-cand-${c.id}-${field}-save-btn`}
                            className="bg-rose-600 hover:bg-rose-700 text-white"
                          >
                            {isSaving ? <Loader2 className="w-3 h-3 animate-spin" /> : <Wrench className="w-3 h-3" />}
                          </Button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              );
            })}
          </div>
        )}
      </Card>
    </div>
  );
};

export default CtcCleanup;
